import React, {
  useEffect,
  useMemo,
  useState,
  useRef,
  useCallback,
} from "react";
import { withTranslation } from "react-i18next";
import { Button, Grid, TextField, useMediaQuery } from "@mui/material";
import PropTypes from "prop-types";

import BaseTable from "Components/Common/BaseTable";
import BreadCrumb from "Components/Common/BreadCrumb";
import SelectField from "Components/Common/SelectField";
import Backdrop from "Components/Common/Backdrop";
import InputFieldText from "Components/Common/InputFieldText";
import { getOptionValue } from "common/enums";

import { convertUTCDateToLocalDate } from "utils/convertUTCDateToLocalDate";

import { useGetBillingCycleOptionsQuery } from "apis/BillingCycle";
import { useLazyDailyMeterReadingsQuery } from "apis/MeterReading";
import { useLazyDownloadExcelQuery } from "slices/api";

const initialQuery = { meterNumber: "", consumerNumber: "" };

const columns = [
  {
    title: "Consumer Number",
    field: "consumerNumber",
  },
  {
    title: "Meter Number",
    field: "meterNumber",
  },
  {
    title: "Set Off Import Energy TOD1",
    field: "setOffImportEnergyTOD1",
  },
  {
    title: "Set Off Export Energy TOD1",
    field: "setOffExportEnergyTOD1",
  },
  {
    title: "Set Off Import Energy TOD3",
    field: "setOffImportEnergyTOD3",
  },
  {
    title: "Set Off Export Energy TOD3",
    field: "setOffExportEnergyTOD3",
  },
  {
    title: "Meter Reading Date",
    renderCell: ({ meterReadingDate }) =>
      meterReadingDate
        ? convertUTCDateToLocalDate(meterReadingDate).format("DD-MM-YYYY")
        : "--",
  },
];

const DailyMeterReadingsReport = ({ t }) => {
  const { data: { data: billingCycleOptions } = {} } =
    useGetBillingCycleOptionsQuery();

  const [downloadExcel, { isLoading: isExcelFileDownloading }] =
    useLazyDownloadExcelQuery();

  const [
    getMeterReadings,
    {
      data: { data: meterReadings } = {},
      isLoading: isMeterReadingLoading,
      isFetching: isMeterReadingFetching,
    },
  ] = useLazyDailyMeterReadingsQuery();

  const sm = useMediaQuery("(max-width: 599px)");

  const [billCycleId, setBillCycleId] = useState("");
  const [query, setQuery] = useState(initialQuery);
  const [isResetPageRegistered, setIsResetPageRegistered] = useState(false);

  const resetPageRef = useRef();

  const handleResetPage = useCallback(resetPageRef.current, [
    isResetPageRegistered,
  ]);

  const updatedBillingCycleOptions = useMemo(
    () =>
      billingCycleOptions?.length
        ? billingCycleOptions
            .filter(
              ({ subscriptionType }) =>
                subscriptionType ===
                getOptionValue("subscriptionTypes", "Electricity")
            )
            .map(({ billDateDuration, id, isPreActive }) => ({
              label: billDateDuration,
              value: id,
              isPreActive,
            }))
        : [],
    [billingCycleOptions]
  );

  useEffect(() => {
    if (updatedBillingCycleOptions.length) {
      const billingCycleId =
        updatedBillingCycleOptions.find(
          ({ status, isPreActive }) => !isPreActive && status !== getOptionValue("billingStatus", "Closed")
        )?.value ??
        updatedBillingCycleOptions.at(-1)?.value ??
        "";
      setBillCycleId(billingCycleId);
      getMeterReadingsData(billingCycleId, initialQuery);
    }
  }, [updatedBillingCycleOptions]);

  const downloadBtnHandler = async () => {
    try {
      if (billCycleId) {
        await downloadExcel({
          endpoint: `/report/billing-cycle/${billCycleId}/daily-meter-reading-excel`,
          sheetName: "Daily Meter Readings",
        });
      }
    } catch (error) {}
  };

  const getMeterReadingsData = async (id, query) => {
    try {
      await getMeterReadings(id, query);
    } catch (error) {}
  };

  return (
    <div className="pb-2">
      <Backdrop
        open={
          isMeterReadingLoading ||
          isMeterReadingFetching ||
          isExcelFileDownloading
        }
      />

      <BreadCrumb title={t("Daily Meter Readings")} pageTitle={t("Billing")} />
      <form
        onSubmit={(e) => {
          e.preventDefault();
          getMeterReadingsData(billCycleId, query);
          handleResetPage();
        }}
      >
        <Grid container columnSpacing={2} className="mb-1">
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <SelectField
              onChange={(_, { value }) => setBillCycleId(value)}
              value={
                updatedBillingCycleOptions.find(
                  (status) => status.value === billCycleId
                ) ?? ""
              }
              renderInput={(params) => {
                delete params.inputProps.id;
                delete params.id;
                return <TextField {...params} label={t("Select bill cycle")} />;
              }}
              options={updatedBillingCycleOptions}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={4} lg={3}>
            <InputFieldText
              label={t("Consumer Number")}
              name="consumerNumber"
              type="number"
              onChange={(e) => {
                setQuery({ ...query, consumerNumber: e.target.value });
              }}
              value={query.consumerNumber}
              placeholder={t("Enter consumer number")}
            />
          </Grid>
          <Grid item lg={3} md={4} sm={6} xs={12}>
            <InputFieldText
              label={t("Meter Number")}
              onChange={(e) => {
                setQuery({ ...query, meterNumber: e.target.value });
              }}
              value={query.meterNumber}
              placeholder={t("Enter meter number")}
            />
          </Grid>

          <Grid item lg={5}>
            <Button variant="outlined" className="mb-2 me-2" type="submit">
              {t("Search")}
            </Button>
            <Button
              variant="outlined"
              onClick={() => {
                getMeterReadingsData(billCycleId, initialQuery);
                setQuery(initialQuery);
                handleResetPage();
              }}
              className="mb-2 me-2"
            >
              {t("Clear")}
            </Button>
            <Button
              variant="contained"
              color="primary"
              className="mb-2 me-2"
              onClick={downloadBtnHandler}
              disabled={!billCycleId}
            >
              {t("Download")}
            </Button>
          </Grid>
        </Grid>
      </form>

      <BaseTable
        data={meterReadings || []}
        columns={columns}
        registerResetPage={(cb) => {
          resetPageRef.current = cb;
          setIsResetPageRegistered((prev) => !prev);
        }}
        displaySrNo
        pageSize={20}
        maxHeight={sm ? "calc(100vh - 330px)" : "calc(100vh - 300px)"}
      />
    </div>
  );
};

DailyMeterReadingsReport.propTypes = { t: PropTypes.func };

export default withTranslation()(DailyMeterReadingsReport);
